"use client"

import React, { useEffect, useMemo, useState } from "react"
import { Card } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { Plus, Edit, Trash2, X, Phone, Search } from "lucide-react"
import { getCSRFToken } from "@/lib/utils"
import OrgLayout from "@/layouts/OrgLayout"
import type { NumberRecord, Facility, Department, User } from "@/types"
import { Link } from "@inertiajs/react"

interface NumbersProps {
  numbers: NumberRecord[]
  facilities?: Facility[]
  departments?: Department[]
  users?: User[]
}

type NumberWithAssociations = NumberRecord & {
  facilityNames: string[]
  departmentNames: string[]
  userNames: string[]
  associationCount: number
}

function Numbers({ numbers: initialNumbers, facilities = [], departments = [], users = [] }: NumbersProps) {
  const [numbers, setNumbers] = useState<NumberRecord[]>(initialNumbers)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")
  const [availableNumbers, setAvailableNumbers] = useState<any[]>([])
  const [selectedNumber, setSelectedNumber] = useState("")
  const [areaCode, setAreaCode] = useState("")

  // Assignment selections for the currently editing number
  const [selectedFacilities, setSelectedFacilities] = useState<number[]>([])
  const [selectedDepartments, setSelectedDepartments] = useState<number[]>([])
  const [selectedUsers, setSelectedUsers] = useState<number[]>([])
  const [selectedAdmins, setSelectedAdmins] = useState<number[]>([])

  const [friendlyEmail, setFriendlyEmail] = useState<string>("");
  // Maps for O(1) lookup
  const facilityMap = useMemo(() => new Map(facilities.map(f => [Number(f.id), f.name])), [facilities])
  const departmentMap = useMemo(() => new Map(departments.map(d => [Number(d.id), d.name])), [departments])
  const userMap = useMemo(() => new Map(users.map(u => [Number(u.id), u.name])), [users])

  // Derived array for UI
  const numbersWithAssociations: NumberWithAssociations[] = useMemo(() => {
    return numbers.map(n => {
      const facilityNames = (n.facility_ids || []).map(id => facilityMap.get(Number(id))).filter(Boolean) as string[]
      const departmentNames = (n.department_ids || []).map(id => departmentMap.get(Number(id))).filter(Boolean) as string[]
      const userNames = (n.user_ids || []).map(id => userMap.get(Number(id))).filter(Boolean) as string[]
      return { ...n, facilityNames, departmentNames, userNames, associationCount: facilityNames.length + departmentNames.length + userNames.length }
    })
  }, [numbers, facilityMap, departmentMap, userMap])

  // Filtered numbers for search
  const filteredNumbers = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return numbersWithAssociations
    return numbersWithAssociations.filter(n =>
      (n.number ?? "").toLowerCase().includes(q) ||
      (n.friendly_name ?? "").toLowerCase().includes(q) ||
      n.facilityNames.join(" ").toLowerCase().includes(q) ||
      n.departmentNames.join(" ").toLowerCase().includes(q) ||
      n.userNames.join(" ").toLowerCase().includes(q)
    )
  }, [searchQuery, numbersWithAssociations])

  // When editingId changes, populate selected assignments from numbers array
  useEffect(() => {
    if (editingId != null) {
      const num = numbers.find(n => n.id === editingId)
      console.log(num)
      setSelectedFacilities(num?.facility_ids?.map(Number) ?? [])
      setSelectedDepartments(num?.department_ids?.map(Number) ?? [])
      setSelectedUsers(num?.user_ids?.map(Number) ?? [])
    } else {
      setSelectedFacilities([])
      setSelectedDepartments([])
      setSelectedUsers([])
    }
  }, [editingId, numbers])

  const fetchAvailableNumbers = async (code?: string) => {
    const area = (code || areaCode).replace(/\D/g, "").slice(0, 3)
    if (!area) return
    try {
      setAvailableNumbers([])
      const res = await fetch(`/signalwire/available_numbers?area_code=${area}`)
      if (!res.ok) throw new Error(`Server returned ${res.status}`)
      const data = await res.json()
      setAvailableNumbers(Array.isArray(data) ? data : [])
    } catch (err) {
      console.error(err)
      alert("Failed to fetch numbers. See console for details.")
    }
  }

  const handlePurchaseOrUpdate = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const csrfToken = getCSRFToken()
    if (!csrfToken) return alert("CSRF token missing")

    try {
      if (editingId == null && selectedNumber) {
        // Purchase
        const res = await fetch("/signalwire/purchase_number", {
          method: "POST",
          headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
          body: JSON.stringify({
            number: selectedNumber,
            friendly_email: friendlyEmail,
            facility_ids: selectedFacilities,
            department_ids: selectedDepartments,
            user_ids: selectedUsers
          }),
        })
        if (!res.ok) throw new Error(`purchase failed: ${res.status}`)
        const purchased = await res.json()
        setNumbers(prev => [...prev, purchased])
      } else if (editingId != null) {
        // Update assignments
        const res = await fetch(`/numbers/${editingId}`, {
          method: "PUT",
          headers: { "Content-Type": "application/json", "X-CSRF-Token": csrfToken },
          body: JSON.stringify({
            facility_ids: selectedFacilities,
            department_ids: selectedDepartments,
            user_ids: selectedUsers,
          }),
        })
        if (!res.ok) throw new Error(`update failed: ${res.status}`)
        setNumbers(prev =>
          prev.map(n =>
            n.id === editingId
              ? { ...n, facility_ids: selectedFacilities, department_ids: selectedDepartments, user_ids: selectedUsers }
              : n
          )
        )
      }
    } catch (err) {
      console.error(err)
      alert("Operation failed; check console.")
    } finally {
      resetForm()
      setDialogOpen(false)
    }
  }

  const resetForm = () => {
    setEditingId(null)
    setSelectedNumber("")
    setAreaCode("")
    setAvailableNumbers([])
  }

  const handleEdit = (num: NumberRecord) => {
    setEditingId(num.id)
    setDialogOpen(true)
  }

  const handleDelete = async (num: NumberRecord) => {
    const csrfToken = getCSRFToken()
    if (!csrfToken) return alert("CSRF token missing")
    if (!confirm(`Delete ${num.number}?`)) return
    try {
      const res = await fetch(`/numbers/${num.id}`, { method: "DELETE", headers: { "X-CSRF-Token": csrfToken } })
      if (!res.ok) throw new Error(`delete failed: ${res.status}`)
      setNumbers(prev => prev.filter(n => n.id !== num.id))
    } catch (err) {
      console.error(err)
      alert("Failed to delete number.")
    }
  }

  useEffect(() => {
    setNumbers(initialNumbers)
  }, [initialNumbers])

  return (
    <div className="space-y-6 w-full">
      {/* Header + Search */}
      <div className="flex flex-col  gap-4">
        <Link href="/">
          <div className="text-xl font-bold">Numbers</div>
        </Link>
        <div className="flex items-center gap-2 flex-1 relative">
          <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search numbers, assigned people or departments..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="pl-9"
          />

          <Dialog open={dialogOpen} onOpenChange={o => { setDialogOpen(o); if (!o) resetForm() }}>
            <DialogTrigger asChild>
              <Button><Plus className="h-4 w-4 mr-1" />Buy Number</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <form onSubmit={handlePurchaseOrUpdate}>
                <DialogHeader>
                  <DialogTitle>{editingId != null ? "Edit Number" : "Purchase Number"}</DialogTitle>
                  <DialogDescription>
                    {editingId != null ? "Update number assignments" : "Search and purchase a new number"}
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 py-4">
                  {!editingId && (
                    <>
                      <Label>Area Code</Label>
                      <div className="flex gap-2">
                        <Input
                          value={areaCode}
                          onChange={e => setAreaCode(e.target.value.replace(/\D/g, "").slice(0, 3))}
                          placeholder="555"
                          maxLength={3}
                        />
                        <Button type="button" onClick={() => fetchAvailableNumbers(areaCode)}>Search</Button>
                      </div>

                      {availableNumbers.length > 0 && (
                        <div className="border rounded-md max-h-40 overflow-y-auto p-2">
                          {availableNumbers.map(n => (
                            <div
                              key={n.phone_number}
                              onClick={() => setSelectedNumber(n.phone_number)}
                              className={`p-2 rounded cursor-pointer hover:bg-accent ${selectedNumber === n.phone_number ? "bg-accent" : ""}`}
                            >
                              {n.friendly_name ?? n.phone_number}
                            </div>
                          ))}
                        </div>
                      )}

                      {selectedNumber && (
                        <div className="mt-2 flex justify-between items-center border p-2 rounded-md bg-muted/30">
                          <span>Selected: {selectedNumber}</span>
                          <Button variant="ghost" size="icon" onClick={() => setSelectedNumber("")}><X className="h-4 w-4" /></Button>
                        </div>
                      )}
                    </>
                  )}

                  <Label>Friendly Email</Label>
                  <Input type="text" value={friendlyEmail} onChange={e => setFriendlyEmail(e.target.value)} placeholder="Friendly Name (optional)" />

                  {/* Assignment pickers */}
                  <AssignedList
                    title="Facilities"
                    available={facilities}
                    selectedIds={selectedFacilities}
                    setSelectedIds={setSelectedFacilities}
                  />
                  <AssignedList
                    title="Departments"
                    available={departments}
                    selectedIds={selectedDepartments}
                    setSelectedIds={setSelectedDepartments}
                  />
                  <AssignedList
                    title="Number Admins (Can delete and archive faxes for everyone)"
                    placeholder="admins"
                    available={users}
                    selectedIds={selectedAdmins}
                    setSelectedIds={setSelectedAdmins}
                  />
                  <AssignedList
                    title="Users"
                    available={users}
                    selectedIds={selectedUsers}
                    setSelectedIds={setSelectedUsers}
                  />

                </div>

                <DialogFooter>
                  <Button type="submit">{editingId != null ? "Update" : "Purchase"}</Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Number list */}
      <div className="space-y-2">
        {filteredNumbers.map(num => (
          <Card key={num.id} className="p-4 flex flex-row items-center justify-between">
            <div className="flex items-center gap-3">
              <Phone className="h-5 w-5 text-muted-foreground" />
              <div>
                <div className="font-medium">{num.number}</div>
                {num.friendly_name && <div className="text-sm text-muted-foreground">{num.friendly_name}</div>}
                <div className="flex flex-wrap gap-2 mt-2">
                  {num.facilityNames.slice(0, 3).map(nName => <Badge key={`f-${nName}`} variant="outline" className="text-xs">{nName}</Badge>)}
                  {num.departmentNames.slice(0, 3).map(nName => <Badge key={`d-${nName}`} variant="outline" className="text-xs">{nName}</Badge>)}
                  {num.userNames.slice(0, 3).map(nName => <Badge key={`u-${nName}`} variant="outline" className="text-xs">{nName}</Badge>)}
                  {num.associationCount > 3 && <Badge variant="secondary" className="text-xs">+{num.associationCount - 3} more</Badge>}
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button size="icon" variant="ghost" onClick={() => handleEdit(num)}><Edit className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" onClick={() => handleDelete(num)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
            </div>
          </Card>
        ))}
        {filteredNumbers.length === 0 && <Card className="p-8 text-center text-muted-foreground">No numbers found.</Card>}
      </div>
    </div>
  )
}

function AssignedList<T extends { id: number; name: string }>({
  title,
  available,
  selectedIds,
  placeholder,
  setSelectedIds,
}: {
  title: string
  available: T[]
  selectedIds: number[]
  placeholder?: string
  setSelectedIds: React.Dispatch<React.SetStateAction<number[]>>
}) {
  const [search, setSearch] = useState("")

  console.log(title)
  console.log(selectedIds)
  const filtered = useMemo(
    () => available.filter(a => a.name?.toLowerCase().includes(search.toLowerCase()) && !selectedIds.includes(Number(a.id))),
    [available, search, selectedIds]
  )

  return (
    <div className="space-y-2">
      <Label>{`${title}`}</Label>

      {selectedIds.length > 0 && (
        <div className="flex flex-wrap gap-2 p-2 border rounded-md bg-muted/30">
          {selectedIds.map(id => {
            const entity = available.find(a => Number(a.id) === Number(id))
            return (
              <Badge key={id} variant="secondary" className="gap-1">
                {entity?.name || `ID ${id}`}
                <button type="button" onClick={() => setSelectedIds(selectedIds.filter(s => s !== id))} className="ml-1 hover:bg-destructive/20 rounded-full">
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            )
          })}
        </div>
      )}

      <div className="relative">
        <Input placeholder={`Search ${placeholder ?? title.toLowerCase()}...`} value={search} onChange={e => setSearch(e.target.value)} />
        {search && filtered.length > 0 && (
          <div className="absolute z-10 w-full mt-1 bg-popover border rounded-md shadow-md max-h-48 overflow-y-auto">
            {filtered.map(a => (
              <button key={a.id} type="button" onClick={() => { if (!selectedIds.includes(Number(a.id))) setSelectedIds([...selectedIds, Number(a.id)]); setSearch("") }} className="w-full text-left px-3 py-2 hover:bg-accent text-sm">
                {a.name}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

Numbers.layout = (page: React.ReactNode) => <OrgLayout activeSection="numbers">{page}</OrgLayout>

export default Numbers

